// C:\Users\home\Desktop\coding_react\damoa-qr\src\pages\en\QrPage_ja.js
import React, { useState, useEffect, useMemo } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import "../QrPage.css"; // CSS는 src/pages에 있으므로 상위 폴더 경로로 변경

function App() {
  const [url, setUrl] = useState("");
  const [qrValue, setQrValue] = useState("");
  const [darkMode, setDarkMode] = useState(false);

  // オプション（必要最低限）
  const [size, setSize] = useState(224); // 128~512
  const [fgColor, setFgColor] = useState("#111827");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [includeMargin, setIncludeMargin] = useState(true);

  // CSR 基準 canonical URL
  const canonicalUrl = useMemo(() => {
    if (typeof window !== "undefined") {
      return window.location.origin + window.location.pathname;
    }
    return null;
  }, []);

  // JSON-LD (SoftwareApplication)
  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "QRコードジェネレーター",
      description:
        "URLを入力してQRコードを生成し、PNGとしてダウンロードできるTooLovesのQRコードジェネレーター",
      applicationCategory: "UtilityApplication",
      operatingSystem: "Web",
      softwareVersion: "1.0.0",
      keywords:
        "QR, QRコード, qrcode.react, QR PNG, QR ダウンロード, TooLoves, Web ユーティリティ",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "JPY",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl]);

  // ダーク/ライトモードに応じてブラウザおよびモバイルのナビゲーションバー色を変更
  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", darkMode ? "#111827" : "#ffffff");
    } else {
      const newMeta = document.createElement("meta");
      newMeta.name = "theme-color";
      newMeta.content = darkMode ? "#111827" : "#ffffff";
      document.head.appendChild(newMeta);
    }
  }, [darkMode]);

  const handleGenerate = (e) => {
    e?.preventDefault?.();
    const v = url.trim();
    if (!v) {
      alert("Webアドレスを入力してください。");
      return;
    }
    setQrValue(v);
  };

  const handleDownload = () => {
    const canvas = document.getElementById("qr-canvas");
    if (!canvas) return;
    const pngUrl = canvas
      .toDataURL("image/png")
      .replace("image/png", "image/octet-stream");
    const link = document.createElement("a");
    link.href = pngUrl;
    link.download = "qrcode.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReset = () => {
    setUrl("");
    setQrValue("");
  };

  // 共有ボタンクリック時にネイティブ共有シートを開く
  const handleShare = async () => {
    if (!qrValue) return;

    const isShareCanceled = (err) => {
      if (!err) return false;
      const name = err.name || "";
      const msg = (err.message || "").toLowerCase();
      return (
        name === "AbortError" ||
        name === "NotAllowedError" ||
        msg.includes("abort") ||
        msg.includes("cancel")
      );
    };

    const canvas = document.getElementById("qr-canvas");

    // 1) ファイル共有を試す（可能ならPNGファイルで）
    if (canvas && "toBlob" in canvas && typeof navigator?.canShare === "function") {
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );
      if (blob) {
        const file = new File([blob], "qrcode.png", { type: "image/png" });
        const shareDataWithFile = {
          files: [file],
          title: "QRコード",
          text: qrValue,
        };

        if (navigator.canShare(shareDataWithFile)) {
          try {
            await navigator.share(shareDataWithFile);
            return; // 成功
          } catch (err) {
            if (isShareCanceled(err)) return; // キャンセルは無視
            // その他のエラーは次のフォールバックへ
          }
        }
      }
    }

    // 2) ファイル共有が不可の場合: URL共有を試す
    if (navigator.share) {
      try {
        await navigator.share({
          title: "QRコード",
          text: "以下のリンクからアクセスしてください。",
          url: qrValue,
        });
        return; // 成功
      } catch (err) {
        if (isShareCanceled(err)) return; // キャンセルは無視
        // その他のエラーは次のフォールバックへ
      }
    }

    // 3) 最終フォールバック: URLをクリップボードにコピー
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(qrValue);
        alert("共有シートをサポートしていないため、リンクをクリップボードにコピーしました。");
      } else {
        alert("このブラウザでは共有をサポートしていません。");
      }
    } catch {
      alert("このブラウザでは共有をサポートしていません。");
    }
  };

  return (
    <div className={`App ${darkMode ? "dark" : "light"}`}>
      <Helmet htmlAttributes={{ lang: "ja" }}>
        <title>QRコードジェネレーター | TooLoves</title>
        <meta
          name="description"
          content="URLを入力してQRコードを生成し、PNGでダウンロードできるQRコードジェネレーター"
        />
        <meta
          name="keywords"
          content="QR, QRコード, qrcode.react, QR PNG, QR ダウンロード, TooLoves, Web ユーティリティ"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {/* 初期 theme-color。ランタイムでは useEffect で同期 */}
        <meta name="theme-color" content={darkMode ? "#111827" : "#ffffff"} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="ja_JP" />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="QRコードジェネレーター | TooLoves" />
        <meta
          property="og:description"
          content="ウェブサイトのURLからQRコードを素早く作成し、PNGで保存"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-qr.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="QRコードジェネレーター | TooLoves" />
        <meta
          name="twitter:description"
          content="ウェブサイトのURLからQRコードを素早く作成し、PNGで保存"
        />
        <meta name="twitter:image" content="/og-qr.png" />

        {/* Canonical */}
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* JSON-LD */}
        <script type="application/ld+json">{jsonLd}</script>
      </Helmet>

      {/* 上部アプリバー */}
      <header className="app-bar">
        <div className="app-bar__inner">
          <Link to="/ja" className="brand-link" aria-label="TooLoves 日本語トップへ">
            TooLoves
          </Link>
          <button
            className="mode-toggle"
            onClick={() => setDarkMode(!darkMode)}
          >
            {darkMode ? "ライトモード" : "ダークモード"}
          </button>
        </div>
      </header>

      {/* 本文 */}
      <main className="container">
        <section className="generator-card">
          <h2 className="card-title">QRコードジェネレーター</h2>
          <p className="card-subtitle">
            URLを入力して「生成」を押すと、下でプレビューとダウンロードができます。
          </p>

          <form className="form" onSubmit={handleGenerate}>
            <label htmlFor="url" className="form-label sr-only">
              ウェブサイトのURL
            </label>
            <div className="input-row">
              <input
                id="url"
                type="text"
                placeholder="ここにウェブサイトのURLを入力"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                autoComplete="off"
                inputMode="url"
              />
              <button type="submit" className="generate-btn">
                QRを生成
              </button>
            </div>

            <div className="options-row">
              <div className="field">
                <label className="form-label">サイズ</label>
                <input
                  type="range"
                  min="128"
                  max="512"
                  step="32"
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                />
                <div className="hint">{size}px</div>
              </div>

              <div className="field">
                <label className="form-label">前景色</label>
                <input
                  type="color"
                  value={fgColor}
                  onChange={(e) => setFgColor(e.target.value)}
                />
              </div>

              <div className="field">
                <label className="form-label">背景色</label>
                <input
                  type="color"
                  value={bgColor}
                  onChange={(e) => setBgColor(e.target.value)}
                />
              </div>

              <div className="field switch">
                <label className="form-label">余白を含む</label>
                <input
                  type="checkbox"
                  checked={includeMargin}
                  onChange={(e) => setIncludeMargin(e.target.checked)}
                />
              </div>
            </div>
          </form>

          <div className="preview-card">
            {qrValue ? (
              <>
                <div className="qr-wrap">
                  <QRCodeCanvas
                    id="qr-canvas"
                    value={qrValue}
                    size={size}
                    bgColor={bgColor}
                    fgColor={fgColor}
                    includeMargin={includeMargin}
                  />
                </div>
                <p className="qr-url">{qrValue}</p>
                <div className="actions">
                  <button type="button" onClick={handleDownload}>
                    PNGをダウンロード
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleReset}
                  >
                    リセット
                  </button>

                  {/* 共有アイコンボタン（テキストなし） */}
                  <button
                    type="button"
                    className="btn-secondary icon-btn"
                    onClick={handleShare}
                    aria-label="共有"
                    title="共有"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      {/* Feather/Lucide share-2 アイコン */}
                      <circle cx="18" cy="5" r="3"></circle>
                      <circle cx="6" cy="12" r="3"></circle>
                      <circle cx="18" cy="19" r="3"></circle>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                    </svg>
                  </button>
                </div>
              </>
            ) : (
              <p className="placeholder">
                URLを入力して「QRを生成」を押してください。
              </p>
            )}
          </div>
        </section>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="法的表示リンク">
          <Link to="/ja/terms" className="footer-link">利用規約</Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <Link to="/ja/privacy" className="footer-link">プライバシーポリシー</Link>
        </nav>
      </footer>
    </div>
  );
}

export default App;